import React from "react";
import _ from "lodash";
import { makeStyles } from "@material-ui/styles";
import { useSpring, animated } from "@react-spring/web";
import { useDispatch, useSelector } from "react-redux";
import { stepTwoTexts } from "../containers/steps/stepTwo";
import TaxonomyData from "../taxonomyData.json";
import { TAXONOMY_TAGS } from "../epics/stepThreeEpics";
import classNames from "classnames";
import ExplainMoreDialog from "./ExplainMoreDialog";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {
  primaryColorLight,
  secondaryColor,
  white,
  grayDark,
  descriptionTextSize,
  bodyTextSize,
  textColor,
  grayedoutGray,
  graphicsDarkRed
} from "../lib/constants";
import { Button } from "@material-ui/core";
import { setPageNumber } from "../epics/pageNumberEpic";
import { StepperTooltip } from "./StepperTooltip";
import { addKeyphrases } from "../epics/canvasEpics";

const calculateTaxonomyPositions = (text, tags) => ({
  type: TAXONOMY_TAGS,
  payload: {
    text: text,
    tags: tags
  }
});
const sentenceScore = (text) => ({ type: "SENTENCE_SCORE", payload: text });

const useStepperStyles = makeStyles({
  stepperContainer: {
    display: "flex",
    flexDirection: "column",
    width: "150px",
    maxHeight: "775px",
    height: "95%",
    overflow: "hidden",
    justifyContent: "center",
    zIndex: "50"
  },

  stepContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    paddingTop: "10px",
    width: "100%",
    marginBottom: "5px"
  },

  label: {
    fontSize: "0.9em",
    color: textColor,
    marginTop: "8px",
    marginBottom: "6px"
  },

  activeLabel: {
    fontWeight: "bold",
    fontSize: bodyTextSize
  },

  divider: {
    height: "auto !important", //"22vh !important",
    paddingBottom: "12px",
    borderLeft: `2px solid ${graphicsDarkRed} !important`,
    width: "100px !important",
    alignItems: "center"
  },

  inactiveDividerSmaller: {
    height: "30px",
    borderLeft: "2px solid " + grayedoutGray,
    marginTop: "7px",
    width: "0px"
  },

  inactiveDividerBigger: {
    height: "30px",
    borderLeft: "2px solid " + grayDark,
    marginTop: "7px",
    width: "0px"
  },

  inactiveFourth: {
    height: "15px",
    borderLeft: "2px solid transparent",
    marginTop: "7px",
    width: "2px"
  },

  description: {
    color: textColor,
    paddingLeft: "8px",
    visibility: "hidden"
  },

  activeDescription: {
    fontSize: descriptionTextSize,
    visibility: "visible !important",
    lineHeight: "1.43",
    paddingBottom: "0px",
    marginBottom: "1px"
  },

  fourthDescription: {
    paddingTop: "7px",
    height: "30% !important",
    visibility: "visible !important",
    width: "100px !important"
  },

  iconStyling: {
    fontSize: "x-large",
    color: grayedoutGray,
    padding: "5px"
  },

  iconContainer: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    marginTop: "7px"
  }
});

// step icon with set size and scaling on activated and hover
// props: current index related to icon and index of currently active step
// returns animated div containing svg icon
function CustomStepIconComponent(props) {
  const { index, activeStep, tags } = props;

  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo);
  const checkedTagsPageThree = useSelector((state) => state.checkedTags);

  const isPulsating = React.useMemo(
    () =>
      (checkedCardStepOne !== "" && index === 1 && activeStep === 0) ||
      (checkedCardStepTwo !== "" && index === 2 && activeStep === 1) ||
      (_.size(checkedTagsPageThree[checkedCardStepOne + " " + checkedCardStepTwo]) !== 0 &&
        index === 3 &&
        activeStep === 2 &&
        tags),
    [checkedCardStepOne, checkedCardStepTwo, checkedTagsPageThree, index, activeStep, tags]
  );

  // scaling with spring
  const [scaleProps, setScaleProps] = useSpring(() => ({
    x: 1,
    config: { mass: 2.5, tension: 300, friction: 15 }
  }));
  const [pulsatingProps] = useSpring(() => ({
    to: { pulse: 1.225 },
    from: { pulse: 1.0 },
    loop: { reverse: true },
    config: {
      duration: 500
    },
    delay: 250
  }));

  // icons after active step are gray
  // icons before active step or with fulfilled conditions to move to next step are in website colors
  let noOffesetColor, offsetColor;

  if (index <= activeStep) {
    noOffesetColor = primaryColorLight;
    offsetColor = secondaryColor;
  } else {
    if (
      (index === 1 && checkedCardStepOne) ||
      (index === 2 && checkedCardStepTwo && activeStep === 1) ||
      (index === 3 && checkedTagsPageThree.length !== 0 && activeStep === 2 && tags)
    ) {
      noOffesetColor = primaryColorLight;
      offsetColor = secondaryColor;
    } else {
      noOffesetColor = white;
      offsetColor = grayDark;
    }
  }

  return (
    // animated div controls scaling of icon on hover and if active
    <animated.div
      onMouseEnter={() => setScaleProps.start({ x: 1.2 })}
      onMouseLeave={() => setScaleProps.start({ x: 1 })}
      style={{
        transform:
          isPulsating === true
            ? pulsatingProps.pulse.to((x) => `scale(${x})`)
            : activeStep === index
            ? "scale(1.4)"
            : scaleProps.x.to((x) => `scale(${x})`)
      }}
    >
      {/* svg controls standard size of icon */}
      <svg
        height="25px"
        viewBox="0 0 38 43"
        version="1.1"
        style={{
          transform: "rotate(240deg)"
        }}
      >
        {/* each icon has its own unique linear gradient component to control the icon's coloring */}
        <defs>
          <linearGradient
            x1="34.5063846%"
            y1="15.1303808%"
            x2="20.8153155%"
            y2="110.664023%"
            id={`combination${index}${activeStep}`}
          >
            <stop stopColor={noOffesetColor} offset="0%" />
            <stop stopColor={offsetColor} offset="100%" />
          </linearGradient>
        </defs>
        {/* generating the svg with path */}
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <path
            d="M0,30.0245126 C0,31.6146901 0.838820037,33.0841534 2.20054545,33.8792422 L16.7994545,42.4036834 C18.16118,43.1987722 19.83882,43.1987722 21.2005455,42.4036834 L35.7994545,33.8792422 C37.16118,33.0841534 38,31.6146901 38,30.0245126 L38,12.9755587 C38,11.3853099 37.16118,9.91591793 35.7994545,9.12075784 L21.2005455,0.596316588 C19.83882,-0.198772196 18.16118,-0.198772196 16.7994545,0.596316588 L2.20054545,9.12075784 C0.838820037,9.91591793 0,11.3853099 0,12.9755587 L0,30.0245126 Z"
            fill={`url(#combination${index}${activeStep})`}
          />
        </g>
      </svg>
    </animated.div>
  );
}

// step labels and descriptions
export const labels = [
  ["Ausgangstext", "1. Wähle einen der vier Texte aus und gehe weiter"],
  ["Vergleichstext", "2. Wähle einen der vier Texte aus und gehe weiter"],
  ["Schlüsselwörter", "3. Wähle beliebig viele Schlüsselwörter"],
  ["3D Modell", "4. Interagiere mit dem Modell"]
];

// actual stepper component
function MoreFeature(props) {
  const pageNumber = useSelector((state) => state.pageNumber);

  const classes = useStepperStyles();
  const activeStep = pageNumber - 1;

  // navigation features
  const dispatch = useDispatch();

  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo);
  const keyphrases = useSelector((state) => state.keyphrases);
  const groups = useSelector((state) => state.group, _.isEqual);
  const taxonomyTagsAndPositions = useSelector((state) => state.taxonomyTagsAndPositions);

  const group = React.useMemo(
    () => groups[checkedCardStepOne + " " + checkedCardStepTwo],
    [groups, checkedCardStepOne, checkedCardStepTwo]
  );
  const tagsAndPositions = React.useMemo(
    () => taxonomyTagsAndPositions[checkedCardStepOne + " " + checkedCardStepTwo],
    [taxonomyTagsAndPositions, checkedCardStepOne, checkedCardStepTwo]
  );

  function onClickStepOne() {
    const txts = [];
    txts.push(checkedCardStepOne);
    stepTwoTexts.forEach((txt) => {
      txts.push(txt[1]);
    });
    dispatch(sentenceScore(txts));
  }

  function onClickStepTwo() {
    const taxData = [];
    Object.keys(TaxonomyData).forEach((key) => taxData.push(...TaxonomyData[key]));
    const data = keyphrases[checkedCardStepOne + " " + checkedCardStepTwo];
    Object.keys(data).forEach((key) => taxData.push(key));
    dispatch(calculateTaxonomyPositions(checkedCardStepOne + " " + checkedCardStepTwo, taxData));
  }

  // controls what happens if user clicks on one step (icon+description)
  // outer switch depends on current page number and inner switch on where user clicked

  // TODO schreib den inneren switch zu einem if/else um!
  const handleChange = (newValue) => {
    switch (pageNumber) {
      case 1: //ausgangstext wählen
        switch (newValue) {
          case 1:
            if (!checkedCardStepOne) return;
            onClickStepOne();
            break;
          default:
            return;
        }
        break;
      case 2:
        switch (newValue) {
          case 0:
            break;
          case 2: // zu schlüsselphrasen
            if (!checkedCardStepTwo) return;
            onClickStepTwo();
            break;
          default:
            return;
        }
        break;
      case 3:
        switch (newValue) {
          case 0:
            break;
          case 1:
            break;
          case 3:
            if (tagsAndPositions !== undefined) {
              _.map(TaxonomyData, (taxa, key) =>
                dispatch(
                  addKeyphrases(
                    taxa,
                    _.map(taxa, (taxon) => tagsAndPositions[0][_.indexOf(tagsAndPositions[1], taxon)]),
                    group ? (group[key] ? group[key] : []) : [],
                    key,
                    checkedCardStepOne + " " + checkedCardStepTwo
                  )
                )
              );
            } else return;
            break;
          default:
            return;
        }
        break;
      case 4:
        switch (newValue) {
          case 0:
            break;
          case 1:
            break;
          case 3:
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    dispatch(setPageNumber(newValue + 1));
  };

  // call to action button event handler in 3d model view -> leads to goodbye screen step 5
  const handleClickEnd = (event) => {
    dispatch(setPageNumber(pageNumber + 1));
  };

  // call to action button event handler in goodbye screen -> leads to step 1
  const handleClickStart = (event) => {
    dispatch(setPageNumber(1));
  };

  return (
    <div className={classes.stepperContainer} key={`wonderful-stepper-container`}>
      {/* iterate over labels and descriptions to generate steps */}
      {_.map(labels, (label, i) => {
        return (
          <React.Fragment key={`some-stuff-thing-fragment-${i}`}>
            <StepperTooltip hoverTarget={i} key={i}>
              {/* outermost div controls click events */}
              <div
                className={classes.stepContainer}
                onClick={() => handleChange(i)}
                key={`stepContainer-${label[0]}-${i}`}
              >
                {/* generate icon for step -> requires index and currently active step index */}
                <CustomStepIconComponent
                  index={i}
                  activeStep={activeStep}
                  key={`icon${i}`}
                  tags={tagsAndPositions !== undefined}
                />
                <span
                  className={classNames(classes.label, i === activeStep && classes.activeLabel)}
                  key={`label-${label}-${i}`}
                >
                  {label[0]}
                </span>

                {/* depending on index different styling 
              all steps but the last step (3d model) have a divider in their inactive state */}
                {i < 3 && (
                  <div
                    key={`divider${i}`}
                    className={classNames(
                      i < activeStep && classes.inactiveDividerSmaller,
                      i > activeStep && classes.inactiveDividerBigger,
                      i === activeStep && classes.divider
                    )}
                  >
                    <p
                      className={classNames(classes.description, i === activeStep && classes.activeDescription)}
                      key={`description${i}`}
                    >
                      {label[1]}
                    </p>
                    {/* if step is active -> display the info icon and add the dialog component */}
                    {i === activeStep && (
                      <div className={classes.iconContainer} key={`popoverContainer${i}`}>
                        <ExplainMoreDialog step={activeStep}>
                          <InfoOutlinedIcon classes={{ root: classes.iconStyling }} key={`infoIcon${i}`} />
                        </ExplainMoreDialog>
                      </div>
                    )}
                  </div>
                )}

                {/* styling for the last step (3d model) */}
                {i === 3 && (
                  <div
                    className={classNames(classes.inactiveFourth, i === activeStep && classes.divider)}
                    key={`divider${i}`}
                  >
                    <p
                      className={classNames(classes.description, i === activeStep && classes.activeDescription)}
                      key={`description${i}`}
                    >
                      {label[1]}
                    </p>
                    {i === activeStep && (
                      <div className={classes.iconContainer} key={`popoverContainer${i}`}>
                        <ExplainMoreDialog step={activeStep}>
                          <InfoOutlinedIcon classes={{ root: classes.iconStyling }} key={`infoIcon${i}`} />
                        </ExplainMoreDialog>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </StepperTooltip>
            {/* call to action button in last step to move to goodbye screen */}
            {i === 3 && i === activeStep && (
              <Button variant="outlined" onClick={handleClickEnd} style={{ marginTop: "10px" }} key={`closeButton${i}`}>
                Fertig
              </Button>
            )}

            {/* call to action button in goodbye screen to move back to step 1 */}
            {i + 1 === 4 && i + 1 === activeStep && (
              <Button
                variant="outlined"
                onClick={handleClickStart}
                style={{ marginTop: "10px" }}
                key={`restartButton${i}`}
              >
                Neu Starten
              </Button>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
export default MoreFeature;
